import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MagnifyingGlass, X } from "@phosphor-icons/react";
import { api } from "@/lib/api";

export const GlobalSearch = () => {
  const [q, setQ] = useState("");
  const [groups, setGroups] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const boxRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (q.trim().length < 2) {
      setGroups([]);
      return;
    }
    setBusy(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q } });
        setGroups(data.groups || []);
        setOpen(true);
      } catch {
        setGroups([]);
      } finally {
        setBusy(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const go = (route) => {
    setOpen(false);
    setQ("");
    navigate(route);
  };

  return (
    <div ref={boxRef} className="relative w-full sm:max-w-sm" data-testid="global-search">
      <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
      <input
        data-testid="global-search-input"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => q.trim().length >= 2 && setOpen(true)}
        placeholder="Search everything…"
        className="h-10 w-full border border-slate-300 bg-white pl-9 pr-8 text-base outline-none transition-colors duration-150 focus:border-[#002FA7] md:text-sm"
      />
      {q && (
        <button
          data-testid="global-search-clear"
          onClick={() => { setQ(""); setGroups([]); }}
          aria-label="Clear search"
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700"
        >
          <X size={14} />
        </button>
      )}

      {open && q.trim().length >= 2 && (
        <div
          data-testid="global-search-results"
          className="absolute left-0 right-0 top-11 z-50 max-h-[70vh] overflow-y-auto border border-border bg-white shadow-xl"
        >
          {busy && groups.length === 0 && (
            <div className="p-3 text-xs text-slate-500">Searching…</div>
          )}
          {!busy && groups.length === 0 && (
            <div data-testid="global-search-empty" className="p-3 text-xs text-slate-500">
              Nothing found for “{q}”
            </div>
          )}
          {groups.map((g) => (
            <div key={g.label}>
              <div className="overline border-b border-border bg-slate-50 px-3 py-1.5 text-slate-500">
                {g.label}
              </div>
              <ul>
                {g.results.map((r) => (
                  <li key={`${g.label}-${r.id}`}>
                    <button
                      data-testid={`search-result-${r.id}`}
                      onClick={() => go(g.route)}
                      className="w-full px-3 py-2 text-left transition-colors duration-150 hover:bg-[#EEF2FF]"
                    >
                      <span className="block truncate text-sm font-medium text-[#0F172A]">{r.title}</span>
                      <span className="block truncate text-xs text-slate-500">{r.subtitle}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
